import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'

import useHotkeys from '@reecelucas/react-use-hotkeys'

import { useNoty } from 'shared/hooks'

import {
  IconUI,
  NotyButtonUI,
  NotyContentUI,
  NotyElementUI,
} from './NotyElement.css'

import {
  ItemShape,
  STATUS_ERROR,
  STATUS_MESSAGE,
  STATUS_SUCCESS,
} from '../../Noty.constants'

/**
 * @typedef {import('../../Noty.constants').TNotyItem} TNotyItem
 * @param {TNotyItem} props
 */
const NotyElement = props => {
  const { content = '', id = 1, status = '', opts = {} } = props

  const {
    timeout = 10000,
    icon = null,
    primaryButtonProps = null,
    dangerButtonProps = null,
  } = opts
  const timeoutRef = useRef()

  const { hideNoty } = useNoty()

  const [visible, setVisible] = useState(true)
  const [deleted, setDeleted] = useState(false)

  useEffect(() => {
    if (!visible || !timeout) return
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setVisible(false)
      timeoutRef.current = null
    }, timeout)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [timeout, visible])

  const handleTransitionEnd = e => {
    if (e.propertyName === 'transform') {
      setDeleted(true)
      hideNoty(id)
    }
  }

  useHotkeys(
    'z',
    () => {
      if (!dangerButtonProps?.onClick) return
      dangerButtonProps.onClick()
      setVisible(false)
    },
    {
      enabled: visible,
    }
  )

  const getTheme = status => {
    switch (status) {
      case STATUS_ERROR:
        return 'red'
      case STATUS_MESSAGE:
        return 'blue'
      case STATUS_SUCCESS:
        return 'green'
      default:
        return 'grey'
    }
  }

  const notyClassnames = cx(visible && 'is-visible', `is-${status}`)

  if (deleted) return null

  return (
    <NotyElementUI
      className={notyClassnames}
      onClick={() => setVisible(false)}
      onTransitionEnd={handleTransitionEnd}
      data-testid="NotyElement"
      role="alert"
    >
      {Boolean(icon) && <IconUI data-testid="NotyIcon" size={24} icon={icon} />}
      <NotyContentUI>{content}</NotyContentUI>
      {Boolean(primaryButtonProps) && (
        <NotyButtonUI color={getTheme(status)} {...primaryButtonProps} />
      )}
      {Boolean(dangerButtonProps) && (
        <NotyButtonUI color="red" {...dangerButtonProps} />
      )}
    </NotyElementUI>
  )
}

NotyElement.propTypes = {
  ...ItemShape,
}

export default NotyElement
